import { get, isNil, map, size, sumBy } from "lodash";
import * as Yup from "yup";

import {
  isFeatureEnabledOrgPreferences,
  isFieldRequiredOrgPreferences,
} from "contexts/DealOrgPreferencesContext";
import { SIZE_TYPE_OPTIONS } from "pages/deal/constants/deal_enums";
import {
  dateSubmitHandler,
  isDealViewPro,
  keyDatesLoadHandler,
  keyDatesSubmitHandler,
  loadPercent,
  parseContactAsSimpleOption,
  parseDealOptionAsString,
  parsePropertyAsSimpleOption,
  retrieveId,
  simpleOptionSubmitHandler,
  submitCurrency,
  submitPercent,
  submitTwoDecimal,
} from "pages/deal/utils/deal_form";
import { VisibilityLevelEnum } from "types/api/deal/dynamic_form/form_field";
import { FormSection } from "types/api/deal/dynamic_form/form_layout";
import { DealStatusEnum } from "types/api/deal/enum";
import { OrgPreferencesReadExtended } from "types/api/deal/preferences";
import { AccessTokenRead } from "types/api/user_management/access_token";
import { DealFeature, DealField, DealType, SizeType } from "types/deal";
import {
  EditableFormFieldMetadata,
  EditableFormObjectMetadata,
} from "types/formMetadata";
import { areFloatsEqual } from "utils/math";

export const DealBaseMetadata: Record<string, EditableFormFieldMetadata> = {
  id: {
    fieldName: "id",
    displayName: "ID",
    initialValue: null,
    _schema: Yup.number().nullable(),
    virtual: true,
  },
  _address_search: {
    fieldName: "_address_search",
    displayName: "Address search",
    initialValue: null,
    _schema: Yup.object().nullable(),
    component: "FormikAddressSearchField",
    detail_component: "AddressFieldDetail",
    virtual: true,
    data_type: "address",
    formCanRemove: false,
  },
  _document_upload: {
    fieldName: "_document_upload",
    displayName: "Documents",
    initialValue: null,
    _schema: Yup.object().nullable(),
    component: "DealDocumentsField",
    detail_component: "DealDocumentsDetail",
    virtual: true,
    formCanEditLayout: false,
    formCanRemove: false,
  },
  transaction_details: {
    fieldName: "transaction_details",
    displayName: "Transaction Details",
    initialValue: [],
    _schema: Yup.array().nullable(),
    component: "TransactionDetailsField",
    detail_component: "TransactionDetailsDetail",
    formCanEditLayout: false,
    formCanRemove: false,
  },
  transaction_parties: {
    fieldName: "transaction_parties",
    displayName: "Transaction Parties",
    initialValue: [],
    _schema: Yup.array().nullable(),
    component: "TransactionPartiesField",
    detail_component: "TransactionPartiesDetail",
    formCanEditLayout: false,
    formCanRemove: false,
  },
  invoices: {
    fieldName: "invoices",
    displayName: "Invoice Schedule",
    initialValue: [],
    _schema: Yup.array().nullable(),
    component: "InvoicesField",
    detail_component: "InvoicesDetail",
    formCanEditLayout: false,
    formCanRemove: false,
  },
  commission_payments_percent_toggle: {
    fieldName: "commission_payments_percent_toggle",
    displayName: "Commission Payment Date Breakdown",
    initialValue: true,
    _schema: Yup.boolean().nullable(),
  },
  commission_split_percent_toggle: {
    fieldName: "commission_split_percent_toggle",
    displayName: "Commission Split",
    initialValue: true,
    _schema: Yup.boolean().nullable(),
  },
  commission_percent_toggle: {
    fieldName: "commission_percent_toggle",
    displayName: "Commission",
    initialValue: false,
    _schema: Yup.boolean().nullable(),
  },
  shared_deal: {
    fieldName: "shared_deal",
    displayName: "Is this client being shared with another broker?",
    initialValue: false,
    _schema: Yup.boolean().nullable(),
    component: "SharedDealField",
    detail_component: "TextFieldDetail",
    data_type: "boolean",
  },
  is_dual_agency: {
    fieldName: "is_dual_agency",
    displayName: "Is there an Outside Broker on this deal?",
    initialValue: false,
    _schema: Yup.boolean().nullable(),
    component: "OutsideBrokerField",
    detail_component: "TextFieldDetail",
    data_type: "boolean",
  },
  probability_override: {
    fieldName: "probability_override",
    displayName: "Probability",
    initialValue: null,
    _schema: Yup.number()
      .nullable()
      .positive("Probability must be greater than 0%.")
      .max(100, "Probability must not exceed 100%"),
    component: "ProbabilityOverrideField",
    detail_component: "TextFieldDetail",
    data_type: "percent_number",
    submitHandler: submitPercent,
    loadHandler: loadPercent,
  },
  address_city: {
    fieldName: "address_city",
    displayName: "City",
    initialValue: null,
    _schema: Yup.string().max(255).nullable(),
  },
  address_country: {
    fieldName: "address_country",
    displayName: "Country",
    initialValue: null,
    _schema: Yup.string().max(255).nullable(),
  },
  address_line_1: {
    fieldName: "address_line_1",
    displayName: "Line 1",
    initialValue: null,
    _schema: (dealOrgPreferences: OrgPreferencesReadExtended) =>
      Yup.string()
        .max(255)
        .nullable()
        .when([], {
          is: () =>
            isFieldRequiredOrgPreferences(
              dealOrgPreferences,
              DealField.ADDRESS
            ),
          then: (schema) => schema.required("Address is required."),
        }),
  },
  address_line_2: {
    fieldName: "address_line_2",
    displayName: "Line 2",
    initialValue: null,
    _schema: Yup.string().max(255).nullable(),
  },
  address_postal_code: {
    fieldName: "address_postal_code",
    displayName: "Zip",
    initialValue: null,
    _schema: Yup.string().max(255).nullable(),
  },
  address_state: {
    fieldName: "address_state",
    displayName: "State",
    initialValue: null,
    _schema: Yup.string().max(2).nullable(),
  },
  asset_type: {
    fieldName: "asset_type",
    displayName: "Asset Type",
    initialValue: null,
    _schema: Yup.string().required(`Asset Type is required.`),
    component: "DealAssetTypeField",
    detail_component: "OptionFieldDetail",
    loadHandler: parseDealOptionAsString,
    submitFieldName: "asset_type_name",
    data_type: "lookup_item",
  },
  client_industry: {
    fieldName: "client_industry",
    displayName: "Client Industry",
    initialValue: null,
    _schema: (dealOrgPreferences: OrgPreferencesReadExtended) =>
      Yup.string()
        .nullable()
        .when([], {
          is: () =>
            isFieldRequiredOrgPreferences(
              dealOrgPreferences,
              DealField.CLIENT_INDUSTRY
            ),
          then: (schema) => schema.required("Client Industry is required."),
        }),
    component: "DealOptionAutocompleteNew",
    detail_component: "OptionFieldDetail",
    loadHandler: parseDealOptionAsString,
    submitFieldName: "client_industry_name",
    data_type: "lookup_item",
  },
  contact: {
    fieldName: "contact",
    displayName: "Primary Contact",
    initialValue: null,
    _schema: (dealOrgPreferences: OrgPreferencesReadExtended) =>
      Yup.object()
        .nullable()
        .when([], {
          is: () =>
            isFieldRequiredOrgPreferences(
              dealOrgPreferences,
              DealField.CONTACT
            ),
          then: (schema) => schema.required("Primary Contact is required."),
        }),
    component: "ContactLookupField",
    detail_component: "ContactFieldDetail",
    submitHandler: simpleOptionSubmitHandler,
    loadHandler: parseContactAsSimpleOption,
    submitFieldName: "contact_id",
    data_type: "lookup_item",
  },
  outside_broker: {
    fieldName: "outside_broker",
    displayName: "Outside Broker Name",
    initialValue: null,
    _schema: Yup.object().nullable(),
    component: "ContactLookupField",
    detail_component: "ContactFieldDetail",
    submitHandler: simpleOptionSubmitHandler,
    loadHandler: parseContactAsSimpleOption,
    submitFieldName: "outside_broker_id",
    data_type: "lookup_item",
  },
  shared_broker: {
    fieldName: "shared_broker",
    displayName: "Shared Broker Name",
    initialValue: null,
    _schema: Yup.object().nullable(),
    component: "ContactLookupField",
    detail_component: "ContactFieldDetail",
    submitHandler: simpleOptionSubmitHandler,
    loadHandler: parseContactAsSimpleOption,
    submitFieldName: "shared_broker_id",
    data_type: "lookup_item",
  },
  close_date: {
    fieldName: "close_date",
    displayName: "Actual Close Date",
    initialValue: null,
    _schema: Yup.date()
      .nullable()
      .typeError(`Close Date must be a date.`)
      .when("status", {
        is: DealStatusEnum.closed,
        then: (schema) => schema.required("Close Date is required."),
      }),
    component: "DatePicker",
    detail_component: "DateFieldDetail",
    submitHandler: dateSubmitHandler,
    data_type: "date",
  },
  close_date_est: {
    fieldName: "close_date_est",
    displayName: "Estimated Close Date",
    initialValue: null,
    _schema: Yup.date()
      .nullable()
      .typeError(`Estimated Close Date must be a date.`)
      .when("status", {
        is: (x: DealStatusEnum) => x !== DealStatusEnum.closed,
        then: (schema) => schema.required(`Estimated Close Date is required.`),
      }),
    component: "DatePicker",
    detail_component: "DateFieldDetail",
    submitHandler: dateSubmitHandler,
    data_type: "date",
  },
  commission: {
    fieldName: "commission",
    displayName: "Actual Commission",
    initialValue: null,
    _schema: Yup.number()
      .min(0, "Commission must be positive.")
      .nullable()
      .typeError(`A value must be entered for Commission.`)
      .when("status", {
        is: DealStatusEnum.closed,
        then: (schema) => schema.required("Commission is required."),
      }),
    component: "TextFieldCurrency",
    detail_component: "CurrencyFieldDetail",
    data_type: "currency",
    submitHandler: submitCurrency,
  },
  commission_est: {
    fieldName: "commission_est",
    displayName: "Estimated Commission",
    initialValue: null,
    _schema: Yup.number()
      .nullable()
      .min(0, "Estimated Commission must be positive.")
      .typeError(`A number is expected for Estimated Commission.`)
      .when("status", {
        is: (x: DealStatusEnum) => x !== DealStatusEnum.closed,
        then: (schema) => schema.required(`Estimated Commission is required.`),
      }),
    component: "TextFieldCurrency",
    detail_component: "CurrencyFieldDetail",
    data_type: "currency",
    submitHandler: submitCurrency,
  },
  _commission_est_percent_toggle: {
    fieldName: "_commission_est_percent_toggle",
    displayName: "",
    initialValue: false,
    _schema: Yup.boolean().nullable(),
    component: "DollarPercentToggle",
    detail_component: "TextFieldDetail",
    virtual: true,
  },
  counterparty_broker_company: {
    fieldName: "counterparty_broker_company",
    displayName: "Outside Brokerage Company",
    initialValue: null,
    _schema: Yup.string().nullable(),
    component: "DealOptionAutocompleteNew",
    detail_component: "OptionFieldDetail",
    loadHandler: parseDealOptionAsString,
    submitFieldName: "counterparty_broker_company_name",
    data_type: "lookup_item",
  },
  deal_type: {
    fieldName: "deal_type",
    displayName: "Deal Type",
    initialValue: null,
    _schema: Yup.string().required(`Deal Type is required.`),
    component: "DealTypeSelectOption",
    detail_component: "DealTypeFieldDetail",
    data_type: "lookup_item",
    formCanRemove: false,
  },
  hire_date: {
    fieldName: "hire_date",
    displayName: "Hire Date",
    initialValue: null,
    _schema: (dealOrgPreferences: OrgPreferencesReadExtended) =>
      Yup.date()
        .nullable()
        .typeError(`Hire Date must be a date.`)
        .when([], {
          is: () =>
            isFieldRequiredOrgPreferences(
              dealOrgPreferences,
              DealField.HIRE_DATE
            ),
          then: (schema) => schema.required("Hire Date is required."),
        }),
    component: "DatePicker",
    detail_component: "DateFieldDetail",
    submitHandler: dateSubmitHandler,
    data_type: "date",
  },
  key_dates: {
    fieldName: "key_dates",
    submitFieldName: "key_dates_input",
    displayName: "Key Dates",
    formCanRemove: false,
    formCanEditLayout: false,
    initialValue: () => [
      {
        date: null,
        date_type: null,
        is_delete: false,
      },
    ],
    _schema: Yup.array().of(
      Yup.object().shape(
        {
          id: Yup.number().nullable(),
          is_delete: Yup.boolean().nullable(),
          date_type: Yup.string()
            .nullable()
            .when(["date", "is_delete"], {
              is: (date: any, is_delete: boolean) => !is_delete && !!date,
              then: (schema) => schema.required("Date Type is required."),
              otherwise: (schema) => schema,
            }),
          date: Yup.date()
            .nullable()
            .typeError("Date is required.")
            .when(["date_type", "is_delete"], {
              is: (date_type: any, is_delete: boolean) =>
                !is_delete && !!date_type,
              then: (schema) => schema.required("Date is required."),
              otherwise: (schema) => schema,
            }),
        },
        [["date_type", "date"]]
      )
    ),
    component: "KeyDatesSection",
    detail_component: "TextFieldDetail",
    submitHandler: keyDatesSubmitHandler,
    loadHandler: keyDatesLoadHandler,
  },
  lease_expiration: {
    fieldName: "lease_expiration",
    displayName: "Lease Expiration",
    initialValue: null,
    _schema: (dealOrgPreferences: OrgPreferencesReadExtended) =>
      Yup.date()
        .nullable()
        .typeError(`Lease Expiration must be a date.`)
        .when(["status", "deal_type"], {
          is: (status: DealStatusEnum, dealType: DealType) =>
            [
              DealType.landlord_agency,
              DealType.tenant_representation,
              DealType.lease_both,
            ].includes(dealType) &&
            status === DealStatusEnum.closed &&
            isFieldRequiredOrgPreferences(
              dealOrgPreferences,
              DealField.LEASE_EXPIRATION
            ),
          then: (schema) => schema.required("Lease Expiration is required."),
        }),
    component: "DatePicker",
    detail_component: "DateFieldDetail",
    submitHandler: dateSubmitHandler,
    data_type: "date",
  },
  lost_date: {
    fieldName: "lost_date",
    displayName: "Lost Date",
    initialValue: null,
    _schema: Yup.date()
      .nullable()
      .typeError(`Lost Date must be a date.`)
      .when("status", {
        is: DealStatusEnum.lost,
        then: (schema) => schema.required("Lost Date is required."),
      }),
    component: "DatePicker",
    detail_component: "DateFieldDetail",
    submitHandler: dateSubmitHandler,
    data_type: "date",
  },
  lost_reason: {
    fieldName: "lost_reason",
    displayName: "Lost Reason",
    initialValue: null,
    _schema: Yup.string()
      .nullable()
      .when("status", {
        is: DealStatusEnum.lost,
        then: (schema) => schema.required("Lost Reason is required."),
      }),
    component: "DealOptionAutocompleteNew",
    detail_component: "OptionFieldDetail",
    loadHandler: parseDealOptionAsString,
    submitFieldName: "lost_reason_name",
    data_type: "lookup_item",
  },
  move_in_date: {
    fieldName: "move_in_date",
    displayName: "Actual Move In Date",
    initialValue: null,
    _schema: Yup.date().nullable().typeError(`Move In Date must be a date.`),
    component: "DatePicker",
    detail_component: "DateFieldDetail",
    submitHandler: dateSubmitHandler,
    data_type: "date",
  },
  move_in_date_est: {
    fieldName: "move_in_date_est",
    displayName: "Estimated Move In Date",
    initialValue: null,
    _schema: Yup.date()
      .nullable()
      .typeError(`Estimated Move In Date must be a date.`),
    component: "DatePicker",
    detail_component: "DateFieldDetail",
    submitHandler: dateSubmitHandler,
    data_type: "date",
  },
  name: {
    fieldName: "name",
    displayName: "Deal Name",
    initialValue: null,
    _schema: Yup.string()
      .max(255, "Deal Name must be less than 256 characters.")
      .required(`Deal Name is required.`),
    component: "TextFieldString",
    detail_component: "TextFieldDetail",
    formCanRemove: false,
    data_type: "text",
  },
  notes_plaintext: {
    fieldName: "notes_plaintext",
    displayName: "Notes",
    initialValue: "",
    _schema: Yup.string()
      .max(25000, "Notes must be less than 25,000 characters.")
      .nullable(),
    component: "TextFieldStringMultiline",
    detail_component: "TextFieldDetail",
    data_type: "text",
  },
  property_record: {
    fieldName: "property_record",
    displayName: "Property",
    initialValue: null,
    _schema: (dealOrgPreferences: OrgPreferencesReadExtended) =>
      Yup.object()
        .nullable()
        .when("deal_type", {
          is: () =>
            isFieldRequiredOrgPreferences(
              dealOrgPreferences,
              DealField.PROPERTY_NAME
            ),
          then: (schema) => schema.required("Property Name is required."),
        }),
    component: "DealPropertyLookupField",
    detail_component: "PropertyFieldDetail",
    submitHandler: simpleOptionSubmitHandler,
    loadHandler: parsePropertyAsSimpleOption,
    submitFieldName: "property_id",
    data_type: "lookup_item",
  },
  relocation_date: {
    fieldName: "relocation_date",
    displayName: "Actual Relocation Date",
    initialValue: null,
    _schema: Yup.date().nullable().typeError(`Relocation Date must be a date.`),
    component: "DatePicker",
    detail_component: "DateFieldDetail",
    submitHandler: dateSubmitHandler,
    data_type: "date",
  },
  relocation_date_est: {
    fieldName: "relocation_date_est",
    displayName: "Estimated Relocation Date",
    initialValue: null,
    _schema: Yup.date()
      .nullable()
      .typeError(`Estimated Relocation Date must be a date.`),
    component: "DatePicker",
    detail_component: "DateFieldDetail",
    submitHandler: dateSubmitHandler,
    data_type: "date",
  },
  shared_broker_company: {
    fieldName: "shared_broker_company",
    displayName: "Shared Brokerage Company",
    initialValue: null,
    _schema: Yup.string().nullable(),
    component: "DealOptionAutocompleteNew",
    detail_component: "OptionFieldDetail",
    loadHandler: parseDealOptionAsString,
    submitFieldName: "shared_broker_company_name",
    data_type: "lookup_item",
  },
  size: {
    fieldName: "size",
    displayName: "Size",
    initialValue: null,
    _schema: (dealOrgPreferences: OrgPreferencesReadExtended) =>
      Yup.number()
        .nullable()
        .min(0, "Size must be positive.")
        .when("status", {
          is: (x: DealStatusEnum) =>
            x === DealStatusEnum.closed &&
            isFieldRequiredOrgPreferences(dealOrgPreferences, DealField.SIZE),
          then: (schema) =>
            schema
              .typeError("Size must be a number.")
              .required(`Size must be a number.`),
        }),
    component: "PropertySizeField",
    detail_component: "SizeFieldDetail",
    submitHandler: submitTwoDecimal,
    data_type: "whole_number",
  },
  size_est: {
    fieldName: "size_est",
    displayName: "Estimated Size",
    initialValue: null,
    _schema: (dealOrgPreferences: OrgPreferencesReadExtended) =>
      Yup.number()
        .nullable()
        .min(0, "Estimated Size must be positive.")
        .when("status", {
          is: (x: DealStatusEnum) =>
            x === DealStatusEnum.open &&
            isFieldRequiredOrgPreferences(dealOrgPreferences, DealField.SIZE),
          then: (schema) =>
            schema
              .typeError("Estimated Size must be a number.")
              .required(`Estimated Size must be a number.`),
        }),
    component: "TextFieldTwoDecimal",
    detail_component: "EstimatedSizeFieldDetail",
    submitHandler: submitTwoDecimal,
    data_type: "whole_number",
  },
  size_type: {
    fieldName: "size_type",
    displayName: "",
    initialValue: SizeType.square_feet,
    _schema: (dealOrgPreferences: OrgPreferencesReadExtended) =>
      Yup.string()
        .nullable()
        .when([], {
          is: () =>
            isFieldRequiredOrgPreferences(dealOrgPreferences, DealField.SIZE),
          then: (schema) =>
            schema.required("Choose a measurement type for the property size."),
        }),
  },
  source_type: {
    fieldName: "source_type",
    displayName: "Deal Source",
    initialValue: null,
    _schema: (dealOrgPreferences: OrgPreferencesReadExtended) =>
      Yup.string()
        .nullable()
        .when([], {
          is: () =>
            isFieldRequiredOrgPreferences(
              dealOrgPreferences,
              DealField.SOURCE_TYPE
            ),
          then: (schema) => schema.required("Deal Source is required."),
        }),
    component: "DealOptionAutocompleteNew",
    detail_component: "OptionFieldDetail",
    data_type: "lookup_item",
    loadHandler: parseDealOptionAsString,
    submitFieldName: "source_type_name",
  },
  stage: {
    fieldName: "stage",
    displayName: "Stage",
    initialValue: null,
    _schema: (dealOrgPreferences: OrgPreferencesReadExtended) =>
      Yup.object()
        .nullable()
        .when([], {
          is: () =>
            isFieldRequiredOrgPreferences(dealOrgPreferences, DealField.STAGE),
          then: (schema) => schema.required("Stage is required."),
        }),
    component: "SelectStageAsync",
    detail_component: "TextFieldDetail",
    submitHandler: retrieveId,
    submitFieldName: "stage_id",
    data_type: "lookup_item",
  },
  status: {
    fieldName: "status",
    displayName: "Status",
    initialValue: DealStatusEnum.open,
    _schema: Yup.string().required(`Status is required.`).nullable(),
    component: "FormikDealStatusChip",
    detail_component: "TextFieldDetail",
    data_type: "lookup_item",
  },
  submarket: {
    fieldName: "submarket",
    displayName: "Submarket",
    initialValue: null,
    _schema: (dealOrgPreferences: OrgPreferencesReadExtended) =>
      Yup.string()
        .nullable()
        .when([], {
          is: () =>
            isFieldRequiredOrgPreferences(
              dealOrgPreferences,
              DealField.SUBMARKET
            ),
          then: (schema) => schema.required("Submarket is required."),
        }),
    component: "DealOptionAutocompleteNew",
    detail_component: "OptionFieldDetail",
    loadHandler: parseDealOptionAsString,
    submitFieldName: "submarket_name",
    data_type: "lookup_item",
  },
  tenant_name: {
    fieldName: "tenant_name",
    displayName: "Tenant Name",
    initialValue: null,
    _schema: (dealOrgPreferences: OrgPreferencesReadExtended) =>
      Yup.string()
        .nullable()
        .when("deal_type", {
          is: (x: DealType) =>
            x === DealType.tenant_representation &&
            isDealViewPro() &&
            isFieldRequiredOrgPreferences(
              dealOrgPreferences,
              DealField.TENANT_NAME
            ),
          then: (schema) => schema.required("Tenant Name is required."),
        }),
    component: "DealOptionAutocompleteNew",
    detail_component: "OptionFieldDetail",
    loadHandler: parseDealOptionAsString,
    submitFieldName: "tenant_name_name",
    data_type: "lookup_item",
  },
  transaction_value: {
    fieldName: "transaction_value",
    displayName: "Actual Transaction Value",
    initialValue: null,
    _schema: Yup.number()
      .min(0, "Actual Transaction Value must be positive.")
      .nullable()
      .typeError(`Actual Transaction Value must be a number.`)
      .when(["status"], {
        is: (status: DealStatusEnum) => status === DealStatusEnum.closed,
        then: (schema) =>
          schema.required("Actual Transaction Value is required."),
      }),
    component: "TextFieldCurrency",
    detail_component: "CurrencyFieldDetail",
    data_type: "currency",
    submitHandler: submitCurrency,
  },
  transaction_value_est: {
    fieldName: "transaction_value_est",
    displayName: "Estimated Transaction Value",
    initialValue: null,
    _schema: Yup.number()
      .min(0, "Estimated Transaction Value must be positive.")
      .nullable()
      .typeError(`Estimated Transaction Value must be a number.`)
      .when(["status"], {
        is: (status: DealStatusEnum) => status !== DealStatusEnum.closed,
        then: (schema) =>
          schema.required("Estimated Transaction Value is required."),
      }),
    component: "TextFieldCurrency",
    detail_component: "CurrencyFieldDetail",
    data_type: "currency",
    submitHandler: submitCurrency,
  },
  commission_split: {
    fieldName: "commission_split",
    displayName: "Commission Split",
    component: "CommissionShareField",
    detail_component: "CommissionShareDetail",
    formCanEditLayout: false,
    formCanRemove: false,
    initialValue: (user: AccessTokenRead) => [
      {
        user_id: user?.user_id,
        broker_role: "executing_and_sourcing",
        estimate_percent: 1,
        estimate_amount: null,
        actual_percent: 1,
        actual_amount: null,
      },
    ],
    _schema: Yup.array()
      .of(
        Yup.object().shape({
          user_id: Yup.number().required("Broker name must be provided."),
          broker_role: Yup.string().nullable(),
          estimate_percent: Yup.number()
            .nullable()
            .when(["status"], {
              is: (status: DealStatusEnum) => status !== DealStatusEnum.closed,
              then: (schema) =>
                schema
                  .min(0, "Estimated Split must be positive.")
                  .max(100, "Estimated Split must be less than 100%."),
            }),
          estimate_amount: Yup.number().nullable(),
          actual_percent: Yup.number()
            .nullable()
            .when(["status"], {
              is: (status: DealStatusEnum) => status === DealStatusEnum.closed,
              then: (schema) =>
                schema
                  .min(0, "Actual Split must be positive.")
                  .max(100, "Actual Split must be less than 100%."),
            }),
          actual_amount: Yup.number().nullable(),
        })
      )
      .test(
        "estimate-split-total-100",
        "estimate-split-total-100",
        (items, testContext) => {
          const isPercent = get(
            testContext,
            "parent.commission_split_percent_toggle",
            false
          );
          const commission_est = get(
            testContext,
            "parent.commission_est",
            null
          );

          if (isPercent) {
            const total = sumBy(items, (item) => item?.estimate_percent || 0);
            return areFloatsEqual(total, 1);
          } else {
            const total = sumBy(items, (item) => item?.estimate_amount || 0);
            return areFloatsEqual(total, commission_est);
          }
        }
      )
      .test(
        "actual-split-total-100",
        "actual-split-total-100",
        (items, testContext) => {
          // Only enforce this test if the status is Closed
          const isClosed =
            get(testContext, "parent.status", DealStatusEnum.open) ===
            DealStatusEnum.closed;
          if (!isClosed) return true;

          const isPercent = get(
            testContext,
            "parent.commission_split_percent_toggle",
            false
          );
          const commission = get(testContext, "parent.commission", null);

          if (isPercent) {
            const total = sumBy(items, (item) => item?.actual_percent || 0);
            return areFloatsEqual(total, 1);
          } else {
            const total = sumBy(items, (item) => item?.actual_amount || 0);
            return areFloatsEqual(total, commission);
          }
        }
      ),
    submitHandler: (x: any) =>
      map(x, (y) => ({
        ...y,
        estimate_amount: submitCurrency(y.estimate_amount),
        actual_amount: submitCurrency(y.actual_amount),
      })),
  },
  commission_payments: {
    fieldName: "commission_payments",
    displayName: "Commission Payments",
    initialValue: [
      { amount: null, percent: null, date: null, paid: false },
      { amount: null, percent: null, date: null, paid: false },
    ],
    _schema: (dealOrgPreferences: OrgPreferencesReadExtended) =>
      Yup.array().when(["status"], {
        is: (status: DealStatusEnum) =>
          status === DealStatusEnum.closed &&
          isFeatureEnabledOrgPreferences(
            dealOrgPreferences,
            DealFeature.COMMISSION_PAYMENTS
          ),
        then: (schema) =>
          schema
            .of(
              Yup.object().shape({
                amount: Yup.number()
                  .nullable()
                  .min(0, "Amount must be positive."),
                date: Yup.date()
                  .nullable()
                  .when(["amount", "percent"], {
                    is: (amount: number | null, percent: number | null) =>
                      (!isNil(amount) && amount > 0) ||
                      (!isNil(percent) && percent > 0),
                    then: (schema) =>
                      schema.required(
                        "Date Due is required when Amount Due is provided."
                      ),
                  }),
                paid: Yup.boolean().nullable(),
                percent: Yup.number()
                  .nullable()
                  .min(0, "Percent must be at least 0%.")
                  .max(1, "Percent must be less than 100%."),
              })
            )
            .test(
              "first-item-amount-required",
              "First Amount Due is required",
              function (value, testContext) {
                const isPercent = get(
                  testContext,
                  "parent.commission_payments_percent_toggle",
                  false
                );
                if (value && value.length > 0) {
                  if (isPercent) {
                    return (
                      value[0].percent !== undefined &&
                      value[0].percent !== null
                    );
                  } else {
                    return (
                      value[0].amount !== undefined && value[0].amount !== null
                    );
                  }
                }
                return true;
              }
            )
            .test(
              "amount-total-100",
              "amount-total-100",
              (items, testContext) => {
                // Only enforce this test if the field is set to Amount
                const isPercent = get(
                  testContext,
                  "parent.commission_payments_percent_toggle",
                  false
                );
                const commission = get(testContext, "parent.commission", null);

                if (isPercent) {
                  const total = sumBy(items, (item) => item?.percent || 0);
                  return areFloatsEqual(total, 1);
                } else {
                  const total = sumBy(items, (item) => item?.amount || 0);
                  return areFloatsEqual(total, commission);
                }
              }
            ),
      }),
    component: "CommissionPaymentSection",
    detail_component: "TextFieldDetail",
    formCanEditLayout: false,
    formCanRemove: false,
    submitHandler: (value: any) =>
      map(value, (x) => ({
        ...x,
        amount: submitCurrency(x.amount),
        date: dateSubmitHandler(x.date),
      })),
  },
  stage_stepper: {
    fieldName: "stage_stepper",
    displayName: "",
    initialValue: null,
    _schema: Yup.number().nullable(),
    component: "StageStepper",
    detail_component: "TextFieldDetail",
    virtual: true,
  },
  created_at: {
    fieldName: "created_at",
    displayName: "Date Created",
    initialValue: null,
    _schema: Yup.date().nullable(),
    component: "DataDateTimeDisplay",
    detail_component: "TextFieldDetail",
    virtual: true,
  },
  updated_at: {
    fieldName: "updated_at",
    displayName: "Date Modified",
    initialValue: null,
    _schema: Yup.date().nullable(),
    component: "DataDateTimeDisplay",
    detail_component: "TextFieldDetail",
    virtual: true,
  },
  created_by: {
    fieldName: "created_by",
    displayName: "Created By",
    initialValue: null,
    _schema: Yup.string().nullable(),
    component: "DataUserDisplay",
    detail_component: "TextFieldDetail",
    virtual: true,
  },
  updated_by: {
    fieldName: "updated_by",
    displayName: "Modified By",
    initialValue: null,
    _schema: Yup.string().nullable(),
    component: "DataUserDisplay",
    detail_component: "TextFieldDetail",
    virtual: true,
  },
};

export const getDefaultDealFormLayout: () => {
  name: string;
  layout: FormSection[];
} = () => ({
  name: "Deal Form",
  layout: [
    {
      name: "General",
      content: [
        {
          column_width: 6,
          field_type: "default",
          field_name: "name",
        },
        { column_width: 6, field_type: "default", field_name: "deal_type" },
      ],
      visible: VisibilityLevelEnum.always,
      visible_condition: null,
    },
    {
      name: "Property",
      content: [
        {
          column_width: 6,
          field_type: "default",
          field_name: "property_record",
        },
        {
          column_width: 6,
          field_type: "default",
          field_name: "asset_type",
        },
        {
          column_width: 6,
          field_type: "default",
          field_name: "size",
        },
        {
          column_width: 6,
          field_type: "default",
          field_name: "submarket",
        },
        {
          column_width: 12,
          field_type: "default",
          field_name: "_address_search",
        },
      ],
      visible: VisibilityLevelEnum.always,
      visible_condition: null,
    },
    {
      name: "Transaction",
      content: [
        {
          column_width: 3,
          field_type: "default",
          field_name: "close_date",
        },
        {
          column_width: 3,
          field_type: "default",
          field_name: "transaction_value",
        },
        {
          column_width: 6,
          field_type: "default",
          field_name: "commission",
        },
        {
          column_width: 12,
          field_type: "default",
          field_name: "transaction_details",
        },
        {
          column_width: 12,
          field_type: "default",
          field_name: "commission_split",
        },
        {
          column_width: 12,
          field_type: "default",
          field_name: "invoices",
        },
      ],
      visible: VisibilityLevelEnum.always,
      visible_condition: null,
    },
    {
      name: "Contacts",
      content: [
        {
          column_width: 12,
          field_type: "default",
          field_name: "transaction_parties",
        },
      ],
      visible: VisibilityLevelEnum.always,
      visible_condition: null,
    },
    {
      name: "Key Dates",
      content: [
        {
          column_width: 12,
          field_type: "default",
          field_name: "key_dates",
        },
      ],
      visible: VisibilityLevelEnum.always,
      visible_condition: null,
    },
    {
      name: "Documents",
      content: [
        {
          column_width: 12,
          field_type: "default",
          field_name: "_document_upload",
        },
      ],
      visible: VisibilityLevelEnum.always,
      visible_condition: null,
    },
  ],
});
export const STATIC_REQUIRED_DEAL_FIELDS = ["name"];
